/**
 * Os botões das camadas da previsão nos trilhos (pedido do usuário,
 * 18/10/2026): no "Próximos dias" o trilho troca as camadas observadas pelas
 * da previsão — Chuva, Temp., Vento e Fumaça, com ícone, como as camadas —
 * em vez de misturar as duas coisas. As observadas não somem: ficam
 * ENCOLHIDAS num botão "Observado" logo abaixo (com quantas estão ligadas) e
 * se estendem com um toque, para ligar alguma por cima da previsão.
 *
 * Como os botões das observadas: um toque põe a variável no mapa (a previsão
 * mostra uma por vez); no desktop, a seta estreita da borda abre a opacidade
 * e os ajustes dela no painel lateral, como a seta das observadas abre o painel.
 * No celular, os ajustes ficam no "⚙" do topo do trilho.
 */
import clsx from 'clsx';
import { ChevronDown, ChevronRight, CloudDrizzle, CloudFog, Dices, Layers, ThermometerSun, Wind, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { getPanels, type PanelDef } from '../ui/panels';
import { variaveisNoTrilho, type VariavelId } from './camadas';
import { abrirPainelLateral, ID_PAINEL_LATERAL } from './PainelLateralDaPrevisao';
import { usePrevisaoStore } from './store';

export const ICONES: Record<VariavelId, LucideIcon> = { chuva: CloudDrizzle, temperatura: ThermometerSun, vento: Wind, fumaca: CloudFog, cenarios: Dices };
export const CURTOS: Record<VariavelId, string> = { chuva: 'Chuva', temperatura: 'Temp.', vento: 'Vento', fumaca: 'Fumaça', cenarios: 'Cenários' };
export const ROTULOS: Record<VariavelId, string> = { chuva: 'Chuva', temperatura: 'Temperatura', vento: 'Vento', fumaca: 'Fumaça', cenarios: 'Cenários' };

/** As variáveis dos trilhos: os cenários (WeatherNext 2) só com o acesso de pesquisa. */
export function useVariaveisNoTrilho(): readonly VariavelId[] {
  return variaveisNoTrilho(usePrevisaoStore((s) => !!s.indice?.modelos.wn2));
}

/** As camadas observadas que têm interruptor (as que o trilho mostra). */
export function camadasObservadas(): PanelDef[] {
  return getPanels().filter((p) => p.layer);
}

/** Conta uma camada ligada (cada camada tem a sua loja; o hook não pode ficar num laço). */
function Ligada(props: { def: PanelDef; aoMudar: (id: string, ligada: boolean) => void }) {
  const ligada = props.def.layer!.useOn();
  const { def, aoMudar } = props;
  useEffect(() => aoMudar(def.id, ligada), [def.id, ligada, aoMudar]);
  return null;
}

/** Quantas camadas observadas estão ligadas: `filhos(n)`. */
export function ContagemDeLigadas(props: { camadas: PanelDef[]; filhos: (n: number) => ReactNode }) {
  const [ligadas, setLigadas] = useState<Record<string, boolean>>(() => Object.fromEntries(props.camadas.map((d) => [d.id, d.layer!.isOn()])));
  const aoMudar = useCallback((id: string, ligada: boolean) => setLigadas((l) => (l[id] === ligada ? l : { ...l, [id]: ligada })), []);
  const n = Object.values(ligadas).filter(Boolean).length;
  return (
    <>
      {props.camadas.map((def) => (
        <Ligada key={def.id} def={def} aoMudar={aoMudar} />
      ))}
      {props.filhos(n)}
    </>
  );
}

/** Põe a variável no mapa; no desktop, com o cartão nos ajustes, ele passa a mostrá-la. */
export function escolherVariavel(v: VariavelId): void {
  const s = usePrevisaoStore.getState();
  if (s.exportando) return;
  s.setVariavel(v);
}

// ---------------------------------------------------------------------------
// Desktop (ui/Rail)

/** Um botão da previsão no trilho do desktop: o mesmo desenho do botão da camada (`RailItem`), em violeta. */
function ItemDaPrevisao(props: { v: VariavelId }) {
  const { v } = props;
  const ativa = usePrevisaoStore((s) => s.variavel === v);
  const lateral = usePrevisaoStore((s) => s.lateral);
  const exportando = usePrevisaoStore((s) => s.exportando);
  const Icon = ICONES[v];
  const mostrando = ativa && lateral;
  const abrirAjustes = () => {
    if (mostrando) return abrirPainelLateral(false);
    escolherVariavel(v);
    abrirPainelLateral(true);
  };
  return (
    <div className="relative h-[52px] w-[60px] shrink-0" data-rail-previsao={v}>
      <button
        type="button"
        onClick={() => escolherVariavel(v)}
        disabled={exportando}
        aria-pressed={ativa}
        aria-label={`Previsão: ${ROTULOS[v]}${ativa ? ' (no mapa)' : ''}`}
        title={ativa ? `${ROTULOS[v]} — no mapa` : `Mostrar a previsão de ${ROTULOS[v].toLowerCase()}`}
        className={clsx(
          'flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-lg pr-3.5 font-medium tracking-tight transition-colors disabled:opacity-40',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-400',
          ativa ? 'bg-violet-500/25 text-white ring-1 ring-violet-400/60 ring-inset hover:bg-violet-500/35' : 'text-ink-300 hover:bg-ink-700/50 hover:text-white',
        )}
      >
        <Icon className={clsx('size-5', ativa ? 'text-violet-200' : 'text-violet-300/70')} aria-hidden />
        <span className="max-w-full truncate text-[9.5px]">{CURTOS[v]}</span>
      </button>
      <button
        type="button"
        onClick={abrirAjustes}
        disabled={exportando}
        aria-expanded={mostrando}
        aria-controls={mostrando ? ID_PAINEL_LATERAL : undefined}
        data-seta-previsao=""
        // Os mesmos verbos da seta das camadas observadas (ui/Rail).
        aria-label={mostrando ? `Recolher o painel ${ROTULOS[v]} (previsão)` : `Abrir o painel ${ROTULOS[v]} (previsão): opacidade e ajustes`}
        title={mostrando ? `Recolher o painel ${ROTULOS[v]}` : `Opacidade e ajustes: ${ROTULOS[v]}`}
        className={clsx(
          'absolute inset-y-0 right-0 flex w-3.5 items-center justify-center rounded-r-lg transition-colors disabled:opacity-40',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-400',
          mostrando ? 'bg-violet-700 text-white' : 'text-ink-500 hover:bg-ink-600 hover:text-white',
        )}
      >
        <ChevronRight className={clsx('size-3 transition-transform', mostrando && 'rotate-180')} aria-hidden />
      </button>
      {mostrando && <span className="absolute inset-y-2.5 left-0 w-0.5 rounded-full bg-violet-400" aria-hidden />}
    </div>
  );
}

/** O grupo da previsão no topo do trilho do desktop: o título e os botões (os cenários, só com o acesso de pesquisa). */
export function PrevisaoNoRail() {
  const variaveis = useVariaveisNoTrilho();
  return (
    <div className="flex flex-col items-center gap-0.5" role="group" aria-label="Camadas da previsão">
      <span className="mb-0.5 text-[10px] font-medium text-violet-300" aria-hidden>
        Previsão
      </span>
      {variaveis.map((v) => (
        <ItemDaPrevisao key={v} v={v} />
      ))}
    </div>
  );
}

/** "Observado": encolhe e estende os botões das camadas observadas, abaixo dos da previsão. */
export function ObservadoNoRail(props: { controla: string }) {
  const aberto = usePrevisaoStore((s) => s.observadoAberto);
  return (
    <ContagemDeLigadas
      camadas={camadasObservadas()}
      filhos={(n) => (
        <button
          type="button"
          onClick={() => usePrevisaoStore.getState().setObservadoAberto(!aberto)}
          aria-expanded={aberto}
          aria-controls={props.controla}
          aria-label={`${aberto ? 'Encolher' : 'Mostrar'} as camadas observadas (${n === 1 ? '1 ligada' : `${n} ligadas`})`}
          title={aberto ? 'Encolher as camadas observadas' : 'Camadas observadas: ligar por cima da previsão'}
          data-rail-observado=""
          className={clsx(
            'relative mt-1 flex h-[46px] w-[60px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border-t border-ink-700 text-[9.5px] font-medium tracking-tight transition-colors',
            'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400',
            aberto ? 'text-white' : 'text-ink-300 hover:bg-ink-700/50 hover:text-white',
          )}
        >
          <span className="relative">
            <Layers className="size-5 text-mata-300" aria-hidden />
            {n > 0 && (
              <span className="absolute -top-1.5 -right-2.5 grid h-3.5 min-w-3.5 place-items-center rounded-full bg-white px-0.5 text-[9px] leading-none font-semibold text-ink-950 tabular-nums" aria-hidden>
                {n}
              </span>
            )}
          </span>
          <span className="flex items-center gap-0.5">
            Observado
            <ChevronDown className={clsx('size-2.5 transition-transform', aberto && 'rotate-180')} aria-hidden />
          </span>
        </button>
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Celular (ui/TrilhoDeCamadas)

/** Um botão da previsão no trilho do celular: o mesmo desenho do `BotaoDoTrilho`, em violeta. */
export function BotaoDaPrevisaoNoTrilho(props: { v: VariavelId; aoTocar: (rotulo: string, botao: HTMLElement) => void }) {
  const { v, aoTocar } = props;
  const ativa = usePrevisaoStore((s) => s.variavel === v);
  const exportando = usePrevisaoStore((s) => s.exportando);
  const Icon = ICONES[v];
  return (
    <button
      type="button"
      onClick={(e) => {
        escolherVariavel(v);
        aoTocar(`${ROTULOS[v]} · previsão`, e.currentTarget);
      }}
      disabled={exportando}
      aria-label={`Previsão: ${ROTULOS[v]}`}
      aria-pressed={ativa}
      data-trilho-previsao={v}
      className={clsx(
        'relative flex size-10 shrink-0 items-center justify-center rounded-[10px] transition-colors select-none disabled:opacity-40',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-violet-400',
        ativa ? 'bg-violet-500/30 text-violet-100 ring-1 ring-violet-300/60 ring-inset' : 'text-violet-300/80 hover:bg-ink-700/60',
      )}
    >
      <Icon className="size-5" aria-hidden />
    </button>
  );
}

/** "Observado" no trilho do celular: o ícone das camadas com quantas estão ligadas; estende a lista abaixo. */
export function ObservadoNoTrilho(props: { controla: string }) {
  const aberto = usePrevisaoStore((s) => s.observadoAberto);
  return (
    <ContagemDeLigadas
      camadas={camadasObservadas()}
      filhos={(n) => (
        <button
          type="button"
          onClick={() => usePrevisaoStore.getState().setObservadoAberto(!aberto)}
          aria-expanded={aberto}
          aria-controls={props.controla}
          aria-label={`${aberto ? 'Encolher' : 'Mostrar'} as camadas observadas (${n === 1 ? '1 ligada' : `${n} ligadas`})`}
          title="Camadas observadas"
          data-trilho-observado=""
          className="relative flex h-10 w-10 shrink-0 items-center justify-center gap-1 rounded-[10px] text-mata-400 transition-colors hover:bg-ink-700/70 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 deitado:col-span-2 deitado:w-full"
        >
          <Layers className="size-5 shrink-0" aria-hidden />
          {/* Deitado (duas colunas, 84 px): o nome escrito; a seta fica só em pé. */}
          <span className="hidden text-[11px] font-semibold text-ink-100 deitado:inline" aria-hidden>
            Observado
          </span>
          {n > 0 && (
            <span
              className="absolute top-0 right-0 grid h-4 min-w-4 place-items-center rounded-full bg-white px-1 text-[10px] leading-none font-semibold text-ink-950 tabular-nums ring-2 ring-ink-900 deitado:top-0.5 deitado:right-0.5 deitado:h-3.5 deitado:min-w-3.5 deitado:text-[9px]"
              aria-hidden
            >
              {n}
            </span>
          )}
          <ChevronDown className={clsx('absolute bottom-0 left-1/2 size-3 -translate-x-1/2 text-ink-300 transition-transform deitado:hidden', aberto && 'rotate-180')} aria-hidden />
        </button>
      )}
    />
  );
}
